<template>
  <div
    :class="[
      'flex h-screen bg-gray-50 transition-colors duration-300 dark:bg-slate-950',
      showLayoutChrome ? 'overflow-hidden' : 'overflow-visible'
    ]"
  >
    <Transition name="nav-progress">
      <div
        v-if="showLoadingBar"
        class="fixed top-0 left-0 right-0 z-50 h-1 overflow-hidden bg-emerald-100 dark:bg-emerald-950"
      >
        <div
          class="h-full w-2/5 bg-gradient-to-r from-emerald-400 via-emerald-500 to-emerald-400 dark:from-emerald-500 dark:via-emerald-400 dark:to-emerald-500 animate-nav-progress"
        />
      </div>
    </Transition>

    <MobileSidebar v-if="showLayoutChrome" />

    <main :class="mainClasses">
      <DesktopTopNavbar v-if="showLayoutChrome" />
      <MobileHeader v-if="showLayoutChrome" />

      <DashboardRouteOutlet :centered-layout="showLayoutChrome" @pending-change="isSuspensePending = $event" />

      <Footer v-if="showLayoutChrome" :centered="showLayoutChrome" />
    </main>
  </div>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, ref, watch } from "vue";

import { useAppConfig } from "@core/composables/data/useAppConfig";
import { useNavigatingState } from "@core/composables/state/useNavigatingState";
import { useRouteContext } from "@core/composables/state/useRouteContext";

import DashboardRouteOutlet from "./DashboardRouteOutlet.vue";
import DesktopTopNavbar from "./DesktopTopNavbar.vue";
import Footer from "./Footer.vue";
import MobileHeader from "./MobileHeader.vue";
import MobileSidebar from "./MobileSidebar.vue";

const props = defineProps<{
  isEmbedded: boolean;
}>();

const route = useRouteContext();
const { config } = useAppConfig();
const { isNavigating } = useNavigatingState();
const isSuspensePending = ref(false);
const showLoadingBar = ref(false);
let loadingBarTimeout: ReturnType<typeof setTimeout> | null = null;

watch(
  () => isNavigating.value || isSuspensePending.value,
  isLoading => {
    if (isLoading) {
      if (loadingBarTimeout) {
        clearTimeout(loadingBarTimeout);
        loadingBarTimeout = null;
      }
      showLoadingBar.value = true;
    } else {
      loadingBarTimeout = setTimeout(() => {
        showLoadingBar.value = false;
      }, 300);
    }
  }
);

onBeforeUnmount(() => {
  if (loadingBarTimeout) {
    clearTimeout(loadingBarTimeout);
    loadingBarTimeout = null;
  }
});

const isChromeFreeLayout = computed(() => route.meta?.layout === "chrome-free");
const showLayoutChrome = computed(() => !props.isEmbedded && !isChromeFreeLayout.value && !config.isNativeClient);
const mainClasses = computed(() => {
  if (!showLayoutChrome.value) {
    return "flex-1 overflow-visible pb-0";
  }
  return "flex flex-1 flex-col overflow-y-auto overscroll-contain pb-0";
});
</script>
